import {
  categoriesToProductsToType,
  FinancialCategory,
} from 'src/financial/financial-data/hooks/useFinancialItems';

/** Accordion key of the reworked Valuation section, shared so forms can open it by default. */
export const VALUATION_SECTION = 'valuation';

type ValuationFields = {
  valueField: string;
  valueLabel?: string;
  valueDateField?: string;
  valueDateLabel?: string;
};

const asValuation = (valueField: string, valueDateField: string): ValuationFields => ({
  valueField,
  valueLabel: 'Total valuation',
  valueDateField,
  valueDateLabel: 'Valuation date',
});

// A debt's outstanding balance and a policy's cover amount are the figures the net-worth
// valuation reads, so they head the Valuation section like any other product's. They are not
// valuations of the item though, so they keep the labels the backend gives them rather than
// being renamed "Total valuation", which would misstate what the number is.
const VALUATION_FIELDS: Record<string, ValuationFields> = {
  SpendingAccount: asValuation('current_balance', 'balance_date'),
  InvestmentAndCash: asValuation('value', 'value_date'),
  Property: asValuation('value', 'value_date'),
  Collectible: asValuation('value', 'value_date'),
  Crypto: asValuation('value', 'value_date'),
  DefinedContributionPension: asValuation('value', 'value_date'),
  DefinedBenefitPension: asValuation('value', 'value_date'),
  PensionAnnuity: asValuation('value', 'value_date'),
  IncomeDrawdownPension: asValuation('value', 'value_date'),
  Mortgage: { valueField: 'remaining_balance', valueDateField: 'remaining_balance_date' },
  Loan: { valueField: 'remaining_balance', valueDateField: 'remaining_balance_date' },
  Protection: { valueField: 'cover_amount' },
};

/**
 * The valuation figure a product is measured by, or undefined when it has none — a state
 * pension is worth its weekly entitlement, not a capital sum, so it gets no Valuation section.
 */
export function valuationFieldsFor(product: string): ValuationFields | undefined {
  return VALUATION_FIELDS[product];
}

function categoryOf(product: string): FinancialCategory | undefined {
  for (const [category, products] of Object.entries(categoriesToProductsToType)) {
    for (const [key, nested] of Object.entries(products)) {
      if (key === product) return category as FinancialCategory;
      if (!Array.isArray(nested) && product in nested) return category as FinancialCategory;
    }
  }
}

const CATEGORY_NOUNS: Record<FinancialCategory, string> = {
  ASSET: 'asset',
  LIABILITY: 'liability',
  PROTECTION: 'policy',
  SPENDING_ACCOUNT: 'account',
};

/** "Save asset", "Save liability", … — the save button names what it is saving. */
export function saveLabelFor(product: string): string {
  const category = categoryOf(product);
  return category ? `Save ${CATEGORY_NOUNS[category]}` : 'Save';
}

// The reworked page puts the client and section in the path above the title, so the title names
// only the product. Taken from the product labels in financialAssetOptions, singularised where
// the label counts things and the page shows exactly one of them.
const REWORK_TITLES: Record<string, string> = {
  SpendingAccount: 'Spending account',
  InvestmentAndCash: 'Cash and investments',
  Property: 'Property',
  Collectible: 'Collectable',
  Crypto: 'Crypto',
  DefinedContributionPension: 'Defined contribution pension',
  DefinedBenefitPension: 'Defined benefit pension',
  PensionAnnuity: 'Annuity',
  IncomeDrawdownPension: 'Income drawdown',
  StatePension: 'State pension',
  Mortgage: 'Mortgage',
  Loan: 'Loan',
  Protection: 'Protection',
};

export function titleFor(product: string, fallback: string): string {
  return REWORK_TITLES[product] ?? fallback;
}
